import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
  PATH_WORKSHOP_AND_EVENTS,
  PLACEHOLDER_IMAGE_AVATAR,
  thunkActionLoading,
} from '@configs'
import { IPhotoMedia } from '@interfaces'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, TableProps, Tooltip } from 'antd'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { workshopEventApi } from 'src/api/workshopEvent'
import { useDebounce } from 'src/hooks/useDebounce'
import { IQuery, IWorkshopEvent } from 'src/interfaces/workshopEvent'
import { getListWorkshopEventAction } from 'src/redux/actions/workshopEvent'

export const useListWorkshopEventTable = () => {
  const navigate = useNavigate()
  const lastParams = useRef<IQuery>()
  const dispatch = useAppDispatch()
  const [deleteId, setDeleteId] = useState('')
  const [sorter, setSorter] = useState<string | undefined>()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string>('')
  const debouncedValue = useDebounce<string | null>(searchString, 800)
  const workshopEvents = useAppSelector(
    (state) => state.workshopEvents.workshopEvents
  )
  const isGettingWorkshopEvent = useAppSelector(
    (state) =>
      state.workshopEvents.loadings[thunkActionLoading.GET_WORKSHOP_EVENT]
  )

  const [totalJob, setTotalJob] = useState<number>()
  const [currentPage, setCurrentPage] = useState<number>()

  const getListJob = async (params: IQuery) => {
    if (
      JSON.stringify(params) === JSON.stringify(lastParams.current) &&
      !deleteId &&
      !sorter
    )
      return
    lastParams.current = params
    try {
      const res = await dispatch(getListWorkshopEventAction(params)).unwrap()
      if (res.success) {
        setTotalJob(res.data.total)
        setCurrentPage(res.data.page)
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const callListJobAfterUpdate = () => {
    if (debouncedValue || sorter) {
      handleClear()
    } else {
      getListJob({
        page: currentPage,
        limit: 10,
        sort: sorter ? `start:${sorter}` : undefined,
      })
    }
  }

  const onDelete = async () => {
    try {
      if (deleteId) {
        setIsDeleting(true)
        const res = await workshopEventApi.deleteWorkshopEvent(deleteId)
        if (res.success) {
          setDeleteId('')
          callListJobAfterUpdate()
          message.success({
            content: res?.message ?? t('common:delete_success'),
          })
        }
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    getListJob({
      search: searchString?.trim() || undefined,
      sort: sorter ? `start:${sorter}` : undefined,
      page: 1,
      limit: 10,
    })
  }, [sorter])

  useEffect(() => {
    getListJob({
      search: debouncedValue?.trim() || undefined,
      sort: sorter ? `start:${sorter}` : undefined,
      page: 1,
      limit: 10,
    })
  }, [debouncedValue])

  const handleClear = () => {
    setSearchString('')
    setSorter(undefined)
  }

  function htmlToText(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  const columns: TableProps<IWorkshopEvent>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      title: 'No',
      key: 'test',
      width: '2%',
    },
    {
      dataIndex: 'background',
      align: 'center',
      title: 'Image',
      key: 'background',
      width: '2%',
      render: (record: IPhotoMedia) => {
        if (record) {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
                alt={record?.url}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },
    {
      dataIndex: 'mobileBackground',
      align: 'center',
      title: 'Image Mobile',
      key: 'mobileBackground',
      width: '2%',
      render: (record: IPhotoMedia) => {
        if (record) {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
                alt={record?.url}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },
    {
      dataIndex: 'name',
      align: 'center',
      key: 'name',
      width: '4%',
      title: 'Event in EN',
      render: (_, record) => {
        return (
          <p className="line-clamp-2 whitespace-pre-line mb-0">{record.name}</p>
        )
      },
    },
    {
      dataIndex: 'description',
      align: 'center',
      key: 'description',
      width: '4%',
      title: 'Description EN',
      render: (_, record) => {
        return (
          <div className="line-clamp-2 whitespace-pre-line">
            {htmlToText(record.description)}
          </div>
        )
      },
    },
    {
      dataIndex: 'isPublished',
      align: 'center',
      key: 'isPublished',
      width: '4%',
      title: 'Status',
      render: (_, record) => {
        return (
          <div className="line-clamp-2 whitespace-pre-line">
            {record.isPublished ? (
              <div className="bg-success text-white font-medium w-fit px-4 py-2 mx-auto rounded-lg">
                Published
              </div>
            ) : (
              <div className="bg-danger text-white font-medium w-fit px-4 py-2 mx-auto rounded-lg">
                UnPublished
              </div>
            )}
          </div>
        )
      },
    },
    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record) => {
        return (
          <div className="flex justify-center w-full space-x-10">
            <Tooltip title={'Edit'}>
              <EditOutlined
                className={`text-lg font-light  cursor-pointer  hover:opacity-80 `}
                onClick={() =>
                  navigate(
                    `${PATH_WORKSHOP_AND_EVENTS}/event-detail/${record.id}`
                  )
                }
              />
            </Tooltip>
            <Tooltip title={'Delete'}>
              <DeleteOutlined
                className={`text-lg font-light  cursor-pointer  hover:opacity-80 !text-red-600`}
                onClick={() => {
                  setDeleteId(record.id?.toString() || '')
                }}
              />
            </Tooltip>
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  return {
    jobs: workshopEvents?.length ? workshopEvents : [],
    columns,
    searchString,
    setSearchString,
    totalJob,
    getListJob,
    currentPage,
    isGettingWorkshopEvent,
    handleClear,
    deleteId,
    setDeleteId,
    onDelete,
    isDeleting,
    sorter,
    setSorter,
  }
}
